import { createSlice } from '@reduxjs/toolkit';
import {
  addPlaylistAudiosEffect,
  createPlaylistEffect,
  deletePlaylistAudioEffect,
  deletePlaylistEffect,
  deletePlaylistImageStatusEffect,
  fetchPlaylistEffect,
  fetchPlaylistImageStatusEffect,
  fetchPlaylistsEffect,
  movePlaylistAudioEffect,
  patchPlaylistEffect,
  updatePlaylistAudioPropsEffect,
  uploadPlaylistImageEffect,
} from 'app/store/Playlists/effects';
import { Playlist, PlaylistsListItem } from 'types/playlists.types';
import { LoadingState } from 'types/enums';
import { Link } from 'types/api.types';

export interface PlaylistsState {
  playlists: PlaylistsListItem[];
  playlist?: Playlist;
  addingAudiosLoadingState: LoadingState;
  imageUploadStatusLink?: Link;
  _links?: {
    next?: Link;
    deletePlaylist?: Link;
    createPlaylistAudio?: Link;
  };
}

export const playlistsInitialState: PlaylistsState = {
  playlists: [],
  addingAudiosLoadingState: LoadingState.PRISTINE,
};

const slice = createSlice({
  name: 'playlists',
  initialState: playlistsInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlaylistEffect.fulfilled, (state, { payload }) => {
        state.addingAudiosLoadingState = LoadingState.FETCHED;
        state.playlist = payload;
      })
      .addCase(createPlaylistEffect.pending, (state) => {
        state.playlist = undefined;
      })
      .addCase(createPlaylistEffect.fulfilled, (state, { payload }) => {
        state.playlist = {
          ...payload,
          playlistAudios: [],
        };
      })
      .addCase(addPlaylistAudiosEffect.pending, (state) => {
        state.addingAudiosLoadingState = LoadingState.FETCHING;
      })
      .addCase(addPlaylistAudiosEffect.fulfilled, (state, { payload }) => {
        state.addingAudiosLoadingState = LoadingState.FETCHED;
        if (!state.playlist) {
          return;
        }
        state.playlist = {
          ...state.playlist,
          playlistAudios: [...payload, ...state.playlist.playlistAudios],
        };
      })
      .addCase(addPlaylistAudiosEffect.rejected, (state) => {
        state.addingAudiosLoadingState = LoadingState.ERROR;
      })
      .addCase(fetchPlaylistsEffect.fulfilled, (state, { payload, meta }) => {
        if (meta.arg?.next) {
          state.playlists = [...state.playlists, ...payload.items];
        } else {
          state.playlists = payload.items;
        }
        state._links = payload._links;
      })
      .addCase(deletePlaylistAudioEffect.fulfilled, (state, { meta }) => {
        if (!state.playlist) {
          return;
        }
        state.playlist.playlistAudios = state.playlist.playlistAudios.filter(
          (audio) => audio.guid !== meta.arg.audioGuid
        );
      })
      .addCase(patchPlaylistEffect.fulfilled, (state, { payload }) => {
        if (!state.playlist) {
          return;
        }
        state.playlist = {
          ...payload,
          playlistAudios: state.playlist.playlistAudios,
        };
      })
      .addCase(movePlaylistAudioEffect.fulfilled, (state, { meta }) => {
        if (!state.playlist) {
          return;
        }
        state.playlist = {
          ...state.playlist,
          playlistAudios: meta.arg.reordered,
        };
      })
      .addCase(deletePlaylistEffect.fulfilled, (state, { meta }) => {
        if (!state.playlists) {
          return;
        }
        state.playlists = state.playlists.filter(
          (playlist) => playlist.guid !== meta.arg.playlistGuid
        );
      })
      .addCase(updatePlaylistAudioPropsEffect.fulfilled, (state, { meta }) => {
        if (!state.playlist) {
          return;
        }

        const updatedAudio = state.playlist.playlistAudios.find(
          (audio) => audio.guid === meta.arg.playlistAudioGuid
        );
        if (updatedAudio) {
          if (meta.arg.note !== undefined) {
            updatedAudio.note = meta.arg.note;
          }
          if (meta.arg.title !== undefined) {
            updatedAudio.title = meta.arg.title;
          }
        }
      })
      .addCase(uploadPlaylistImageEffect.fulfilled, (state, { payload }) => {
        state.imageUploadStatusLink = payload;
      })
      .addCase(
        fetchPlaylistImageStatusEffect.fulfilled,
        (state, { payload }) => {
          if (
            payload?.playlist?.guid &&
            state.playlist &&
            state.playlist.guid
          ) {
            state.playlist = payload.playlist;
          }
        }
      )
      .addCase(
        deletePlaylistImageStatusEffect.fulfilled,
        (state, { meta: { arg } }) => {
          if (state.playlist && arg === state.playlist?.guid) {
            state.playlist.images = [];
          }
        }
      );
  },
});

export const playlistsReducer = slice.reducer;
